import { Link } from '@tanstack/react-router';
import { AlertCircle, Loader2, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface FormShellProps<TFields extends FieldValues> {
  form: UseFormReturn<TFields>;
  onSubmit: (e?: React.BaseSyntheticEvent) => void | Promise<void>;
  submitLabel?: string;
  cancelTo?: string;
  onCancel?: () => void;
  /** Shows the spinner AND disables submit. Use only for in-flight mutations. */
  isSubmitting?: boolean;
  /** Disables submit without showing the spinner. Use for validity / dirty gates. */
  disabled?: boolean;
  topError?: string;
  children: ReactNode;
  className?: string;
}

export function FormShell<TFields extends FieldValues>({
  form,
  onSubmit,
  submitLabel = 'Save changes',
  cancelTo,
  onCancel,
  isSubmitting,
  disabled,
  topError,
  children,
  className,
}: FormShellProps<TFields>) {
  const submitting = isSubmitting ?? form.formState.isSubmitting;
  const submitDisabled = submitting || (disabled ?? false);
  const [dismissedError, setDismissedError] = useState(false);
  const showError = Boolean(topError) && !dismissedError;

  return (
    <form onSubmit={onSubmit} noValidate className={cn('flex flex-col gap-6', className)}>
      {showError ? (
        <div
          role="alert"
          className={cn(
            'border-error-300 bg-error-500/5 text-error-700 flex items-start gap-3 rounded-md border p-4',
          )}
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
          <div className="text-body-md flex-1">{topError}</div>
          <button
            type="button"
            aria-label="Dismiss error"
            onClick={() => {
              setDismissedError(true);
            }}
            className="text-error-700/80 hover:bg-error-500/10 rounded-sm p-0.5"
          >
            <X className="h-3 w-3" strokeWidth={1.5} />
          </button>
        </div>
      ) : null}

      <div className="flex flex-col gap-5">{children}</div>

      <div className="flex items-center justify-end gap-3 pt-2">
        {cancelTo ? (
          <Button asChild variant="ghost" size="md">
            <Link to={cancelTo as never}>Cancel</Link>
          </Button>
        ) : onCancel ? (
          <Button variant="ghost" size="md" type="button" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        <Button type="submit" size="md" disabled={submitDisabled} className="min-w-35">
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              {submitLabel}
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </form>
  );
}
